import { DeleteOutlined } from "@ant-design/icons";
import { Button, Input, Select } from "antd";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import {
    PropertyModifyChangeType,
    PropertyModifyConditionType,
    PropertyModifyValueCompereType,
    RequestConnectionPropertyType,
} from "types/enums";

import { MethodPropertyBodySettingFormData } from "./formSchema";

import FormFieldData = App.Data.FormFieldData;
import ResponseConnectionPropertyData = App.Data.ResponseConnectionPropertyData;

const defaultMethodProperty: MethodPropertyBodySettingFormData = {
    id: null,
    change_type: PropertyModifyChangeType.ToInt,
    condition_type: PropertyModifyConditionType.StringStartsWith,
    value_compere_type: PropertyModifyValueCompereType.Value,
    condition_value: null,
    condition_value2: null,
    function_parameter: null,
    lead_value_accessor: null,
    value_compere_data_accessor: null,
    value_to_change: null,
    request_connection_property_id: null,
    response_connection_property_id: null,
    request_header_config_id: null,
    created_at: null,
    updated_at: null,
};

const MethodPropertyModifySettingFormContent: FC<{
    leadFields?: Array<FormFieldData | ResponseConnectionPropertyData>;
    propertyType?: RequestConnectionPropertyType;
    propertyPath: string;
}> = ({ leadFields, propertyType, propertyPath }) => {
    const { t } = usePaldockTranslation("forms.method-property-modify-settings");
    const { t: tC } = usePaldockTranslation("common");
    const { control } = useGeneralForm();
    const { fields, append, remove } = useFieldArray({
        control,
        name: propertyPath + ".requestConnectionPropertyModify",
    });

    const conditionDisabledTypes = [
        PropertyModifyConditionType.IsNotEmpty,
        PropertyModifyConditionType.IsEmpty,
        PropertyModifyConditionType.IsTrue,
        PropertyModifyConditionType.IsFalse,
        PropertyModifyConditionType.Always,
    ];

    const changeDisabledTypes = [
        PropertyModifyChangeType.ToFloat,
        PropertyModifyChangeType.ToBool,
        PropertyModifyChangeType.ToInt,
        PropertyModifyChangeType.DateToUnix,
        PropertyModifyChangeType.DoNotSend,
        PropertyModifyChangeType.CzNinToBirthdate,
        PropertyModifyChangeType.CzNinToGender,
        PropertyModifyChangeType.ToBase64,
        PropertyModifyChangeType.FromBase64,
        PropertyModifyChangeType.FirstRegexMatch,
        PropertyModifyChangeType.FormatDate,
        PropertyModifyChangeType.ModifyDate,
        PropertyModifyChangeType.LoadFromLead,
    ];

    function shouldDisableField<T extends string>(currentType: T, disabledList: T[]): boolean {
        return disabledList.includes(currentType);
    }

    const allProperties =
        useWatch({
            control,
            name: propertyPath + ".requestConnectionPropertyModify",
        }) ?? [];

    return (
        <div>
            {fields?.map((field, index) => {
                // const fieldNames = buildArrayFieldNames<MethodPropertyBodySettingFormData>(
                //     "requestConnectionPropertyModify",
                //     index
                // );

                const currentConditionType = allProperties[index]?.condition_type;
                const currentChangeType = allProperties[index]?.change_type;
                const currentComparisonType = allProperties[index]?.value_compere_type;

                return (
                    <div key={field.id} style={{ marginBottom: "16px", border: "1px solid #ddd", padding: "16px" }}>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.requestConnectionPropertyModify.${index}.value_compere_type`}
                            label={t("fields.value_compere_type.label")}
                            required
                        >
                            <Select
                                placeholder={t("fields.value_compere_type.placeholder")}
                                options={Object.values(PropertyModifyValueCompereType).map((type) => ({
                                    value: type,
                                    label: type,
                                }))}
                            />
                        </FormItem>

                        {currentComparisonType === PropertyModifyValueCompereType.DataAccessor &&
                            propertyType === RequestConnectionPropertyType.Combine && (
                                <FormItem
                                    control={control}
                                    name={`${propertyPath}.requestConnectionPropertyModify.${index}.value_compere_data_accessor`}
                                    label={t("fields.value_compere_data_accessor.label")}
                                >
                                    <Select
                                        placeholder={t("fields.value_compere_data_accessor.placeholder")}
                                        options={leadFields?.map((field) => ({
                                            value: field.system_name,
                                            label: field.system_name,
                                        }))}
                                    />
                                </FormItem>
                            )}

                        <FormItem
                            control={control}
                            name={`${propertyPath}.requestConnectionPropertyModify.${index}.condition_type`}
                            label={t("fields.condition_type.label")}
                            required
                        >
                            <Select
                                placeholder={t("fields.value_compere_type.placeholder")}
                                options={Object.values(PropertyModifyConditionType).map((type) => ({
                                    value: type,
                                    label: type,
                                }))}
                            />
                        </FormItem>

                        <FormItem
                            control={control}
                            name={`${propertyPath}.requestConnectionPropertyModify.${index}.condition_value`}
                            label={t("fields.condition_value.label")}
                            disabled={shouldDisableField(currentConditionType, conditionDisabledTypes)}
                        >
                            <Input />
                        </FormItem>

                        {(currentConditionType === PropertyModifyConditionType.Between ||
                            currentConditionType === PropertyModifyConditionType.BetweenInclude) && (
                            <FormItem
                                control={control}
                                name={`${propertyPath}.requestConnectionPropertyModify.${index}.condition_value2`}
                                label={t("fields.condition_value2.label")}
                            >
                                <Input />
                            </FormItem>
                        )}

                        <FormItem
                            control={control}
                            name={`${propertyPath}.requestConnectionPropertyModify.${index}.change_type`}
                            label={t("fields.change_type.label")}
                            required
                        >
                            <Select
                                placeholder={t("fields.value_compere_type.placeholder")}
                                options={Object.values(PropertyModifyChangeType).map((type) => ({
                                    value: type,
                                    label: type,
                                }))}
                            />
                        </FormItem>

                        <FormItem
                            control={control}
                            name={`${propertyPath}.requestConnectionPropertyModify.${index}.value_to_change`}
                            label={t("fields.value_to_change.label")}
                            disabled={shouldDisableField(currentChangeType, changeDisabledTypes)}
                        >
                            <Input />
                        </FormItem>

                        {[
                            PropertyModifyChangeType.Match,
                            PropertyModifyChangeType.FirstRegexMatch,
                            PropertyModifyChangeType.FormatDate,
                            PropertyModifyChangeType.ModifyDate,
                            PropertyModifyChangeType.RegexReplace,
                        ].includes(currentChangeType) && (
                            <FormItem
                                control={control}
                                name={`${propertyPath}.requestConnectionPropertyModify.${index}.function_parameter`}
                                label={t("fields.function_parameter.label")}
                            >
                                <Input />
                            </FormItem>
                        )}

                        {currentChangeType === PropertyModifyChangeType.LoadFromLead && (
                            <FormItem
                                control={control}
                                name={`${propertyPath}.requestConnectionPropertyModify.${index}.lead_value_accessor`}
                                label={t("fields.lead_value_accessor.label")}
                            >
                                <Select
                                    placeholder={t("fields.lead_value_accessor.placeholder")}
                                    options={leadFields?.map((field) => ({
                                        value: field.system_name,
                                        label: field.system_name,
                                    }))}
                                />
                            </FormItem>
                        )}

                        <Button danger onClick={() => remove(index)} icon={<DeleteOutlined />} />
                    </div>
                );
            })}

            <Button
                type="primary"
                onClick={() => {
                    append(defaultMethodProperty);
                }}
            >
                {tC("add")}
            </Button>
        </div>
    );
};

export const MethodPropertyModifySettingsForm: FC<{
    leadFields?: Array<FormFieldData | ResponseConnectionPropertyData>;
    propertyType?: RequestConnectionPropertyType;
    propertyPath: string;
}> = ({ leadFields, propertyType, propertyPath }) => {
    return (
        <MethodPropertyModifySettingFormContent
            leadFields={leadFields}
            propertyType={propertyType}
            propertyPath={propertyPath}
        />
    );
};
